import { useEffect } from 'react';
import { useEditor, EditorContent, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Unlink, Undo2, Redo2 } from 'lucide-react';

const Btn = ({ onClick, active, label, children, testid }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={label} aria-label={label} data-testid={testid}
    className={`w-8 h-8 grid place-items-center rounded-md transition-colors ${active ? 'bg-[#111] text-white' : 'text-[#555] hover:bg-[#f1ece3]'}`}>
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({
      heading: { levels: [2, 3] }, code: false, codeBlock: false, strike: false, underline: false, horizontalRule: false,
      link: { openOnClick: false, autolink: true, protocols: ['mailto'] },
    })],
    content: value || '',
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? '' : ed.getHTML()),
    editorProps: { attributes: { class: 'article-body rte-content min-h-[320px] px-5 py-4 focus:outline-none text-[15px] leading-[1.8] text-[#111]', 'data-testid': 'rte-content' } },
  });
  const st = useEditorState({
    editor,
    selector: ({ editor: e }) => e && ({
      bold: e.isActive('bold'), italic: e.isActive('italic'), h2: e.isActive('heading', { level: 2 }), h3: e.isActive('heading', { level: 3 }),
      ul: e.isActive('bulletList'), ol: e.isActive('orderedList'), quote: e.isActive('blockquote'), link: e.isActive('link'),
    }),
  });
  useEffect(() => {
    if (editor && value !== undefined && value !== (editor.isEmpty ? '' : editor.getHTML())) editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const setLink = () => {
    const prev = editor.getAttributes('link').href || 'https://';
    const url = window.prompt('Link URL (https://… or /page)', prev);
    if (url === null) return;
    if (!url.trim() || url === 'https://') { c().extendMarkRange('link').unsetLink().run(); return; }
    c().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };
  return (
    <div className="border border-[#eae4d8] rounded-xl bg-white overflow-hidden" data-testid="rte">
      <div className="flex flex-wrap gap-1 p-2 border-b border-[#eae4d8] bg-[#FAF8F5]">
        <Btn label="Heading" active={st?.h2} onClick={() => c().toggleHeading({ level: 2 }).run()} testid="rte-h2"><Heading2 size={16} /></Btn>
        <Btn label="Subheading" active={st?.h3} onClick={() => c().toggleHeading({ level: 3 }).run()} testid="rte-h3"><Heading3 size={16} /></Btn>
        <Btn label="Bold" active={st?.bold} onClick={() => c().toggleBold().run()} testid="rte-bold"><Bold size={15} /></Btn>
        <Btn label="Italic" active={st?.italic} onClick={() => c().toggleItalic().run()} testid="rte-italic"><Italic size={15} /></Btn>
        <Btn label="Link" active={st?.link} onClick={setLink} testid="rte-link"><Link2 size={15} /></Btn>
        {st?.link && <Btn label="Remove link" onClick={() => c().unsetLink().run()} testid="rte-unlink"><Unlink size={15} /></Btn>}
        <Btn label="Bulleted list" active={st?.ul} onClick={() => c().toggleBulletList().run()} testid="rte-ul"><List size={16} /></Btn>
        <Btn label="Numbered list" active={st?.ol} onClick={() => c().toggleOrderedList().run()} testid="rte-ol"><ListOrdered size={16} /></Btn>
        <Btn label="Quote" active={st?.quote} onClick={() => c().toggleBlockquote().run()} testid="rte-quote"><Quote size={15} /></Btn>
        <span className="flex-1" />
        <Btn label="Undo" onClick={() => c().undo().run()} testid="rte-undo"><Undo2 size={15} /></Btn>
        <Btn label="Redo" onClick={() => c().redo().run()} testid="rte-redo"><Redo2 size={15} /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
